import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { ICreateReportDTO, IReportDetailsDTO, IReportListItemDTO, IReportsDAO, IUpdateReportDTO, ReportType } from '../../../../../api/reports.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import Text from '../../../../global/text/text';
import { Space } from '../../../../../constants/html-character-entities';
import { alertServerError, handleApiException } from '../../../../global/modal/alerts-host/alert-templates';
import { ReportFormDataVM } from './report-form-data.viewmodel';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { reportTypeLabels } from '../../../../../constants/report-labels.constants';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';

class ReportFormVM {
    @mobx.observable
    public saving: boolean;
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public formDataVM: ReportFormDataVM;

    constructor(
        private eventId: number,
        private accountId: number,
        private saveCompletedCallback: () => void,
        private deleteCompletedCallback: () => void,
        private reportData?: IReportDetailsDTO,
        @IoC.Inject private readonly reportsDAO: IReportsDAO = IoC.Container.get(IReportsDAO),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.formDataVM = new ReportFormDataVM(this.reportData);
    }

    public isSaving() {
        return this.saving;
    }

    public getFormDataVM(): ReportFormDataVM {
        return this.formDataVM;
    }

    public async handleApiType(requestParams: ICreateReportDTO | IUpdateReportDTO, requestHandler: (params) => Promise<void | IReportListItemDTO>) {
        await requestHandler({
            eventId: this.eventId,
            accountId: this.accountId,
            ...requestParams
        });
    }

    @mobx.action
    public handleSubmit = async (intl: IntlShape) => {
        this.getFormDataVM().serverErrorMessage = '';
        const isFormInvalid = !this.getFormDataVM().isFormValid(intl);
        if (isFormInvalid) {
            this.getFormDataVM().allowToShowValidationErrors();
            return false;
        }
        try {
            this.saving = true;

            if (this.formDataVM.isEdit) {
                await this.handleApiType(
                    this.formDataVM.toApiUpdateReportDTO(),
                    params => this.reportsDAO.updateReport(params)
                );
                this.trackUploadEditFileMixPanel(this.formDataVM.toApiUpdateReportDTO(), TRACKING_EVENTS.EDIT_REPORT);
            } else {
                await this.handleApiType(
                    this.formDataVM.toApiCreateReportDTO(),
                    params => this.reportsDAO.createReport(params)
                );
                this.trackUploadEditFileMixPanel(this.formDataVM.toApiCreateReportDTO(), TRACKING_EVENTS.UPLOAD_REPORT);
            }

            this.saveCompletedCallback();
            return true;
        } catch (error) {
            this.onCreateReportError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    private onCreateReportError(error): void {
        const apiError = this.errorParsingService.extractAPIError(error);
        if (apiError) {
            if (error.response?.status === 403) {
                handleApiException(error);
            } else {
                this.getFormDataVM().serverErrorMessage = apiError.errorMessage;
            }
        } else {
            this.alertsService.alert({message: this.errorParsingService.extractMessage(error)});
        }
    }

    private deleteReportApiCall = async (intl: IntlShape) => {
        try {
            await this.reportsDAO.deleteReport({
                accountId: this.accountId,
                eventId: this.eventId,
                assetMetadataId: this.formDataVM.assetMetadataId
            });
            this.trackDeleteFileMixPanel();
            this.deleteCompletedCallback();
        } catch (error) {
            alertServerError(intl.formatMessage({
                id: 'oe.common.error',
                defaultMessage: 'Error',
                description: 'Label text',
            }), this.errorParsingService.extractMessage(error));
        }
    };

    public deleteReport = (intl: IntlShape) => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.reports.deleteThisFile'
                        message='delete this file'
                        description='Label text'
                    />?
                    <Space />
                    <Text
                        className='alert-message'
                        id='oe.alerts.thisActionCannotBeUnDone'
                        description='Alert message'
                        message='This cannot be un-done.'
                    />
                </>
            ),
            confirmButtonProps: {
                text: intl.formatMessage({
                    id: 'oe.reports.reportDelete',
                    defaultMessage: 'Yes, delete',
                    description: 'Label text'
                }),
                modifier: 'confirm-warn',
                handler: () => this.deleteReportApiCall(intl),
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private getCommonMixpanelProperties = (assetType: ReportType) => ({
        'Account Name': this.globalStorage.getEvent()?.accountName,
        'Event ID': this.eventId.toString(),
        'Report Type': (reportTypeLabels[assetType] || reportTypeLabels[ReportType.EVENT_REPORT]).defaultMessage,
    });

    public trackUploadEditFileMixPanel = (params: ICreateReportDTO | IUpdateReportDTO, eventName: TRACKING_EVENTS) => {
        let fileName = this.formDataVM.assetName;
        if ('reportFile' in params) {
            fileName = params.reportFile.name;
        }

        const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1);

        let properties: { [key: string]: string } = {
            ...this.getCommonMixpanelProperties(params.assetType),
            'File': fileExtension,
        };

        if (eventName === TRACKING_EVENTS.EDIT_REPORT) {
            properties = {
                ...properties,
                'Report name edited': convertBoolTypeForMixPanel(params.alias !== this.formDataVM.initValues.alias),
                'Report type edited': convertBoolTypeForMixPanel(params.assetType !== this.formDataVM.initValues.assetType)
            };
        }

        this.trackingService.get().track(eventName, properties);
    };

    public trackDeleteFileMixPanel = () => {
        this.trackingService.get().track(
            TRACKING_EVENTS.DELETE_REPORT,
            this.getCommonMixpanelProperties(this.formDataVM.initValues.assetType)
        );
    };
}

export { ReportFormVM };
